import {
  computed, onMounted, onBeforeUnmount, ref,
} from 'vue';
import { useResizeObserver, useIntersectionObserver } from '@vueuse/core';

/**
 * A composable for image setup and related template ref
 * @param {object} options
 * @param {import('vue').Ref<Boolean | String>} options.fetchPriority - set fetchpriority attribute on img
 * @param {import('vue').Ref<Object>} options.pt - pass attributes to DOM elements inside the component
 * @param {import('vue').Ref<Boolean>} options.skeleton - showing skeleton while loading image
 * @param {import('vue').Ref<Boolean>} options.lazyLoad - Indicate whether need lazy loading image
 * @param {import('vue').Ref<Boolean>} options.nativeLazy - Indicate whether to use native lazyloading behavior instead of intersectionObserver
 * @param {import('vue').Ref<Number | 0.8>} options.aspectRatio - Specified image aspect ratio
 */
export default function useImageSetup({
  fetchPriority = ref(false),
  pt = ref({}),
  skeleton = ref(false),
  lazyLoad = ref(true),
  nativeLazy = ref(false),
  aspectRatio = ref(0.8),
}) {
  // pt value take precedence over deprecated prop fetchPriority
  const lazyLoaded = ref(pt.value?.img?.fetchPriority ? pt.value?.img?.fetchPriority === 'high'
    : fetchPriority.value === true || fetchPriority.value === 'high');
  const enableSkeletonAnimation = ref(false);
  const showSkeleton = ref(skeleton.value);
  let unobserveFn;
  const pictureContainer = ref(null);

  const setupIntersect = () => {
    if (!lazyLoaded.value && !nativeLazy.value) {
      const { stop } = useIntersectionObserver(pictureContainer.value,
        ([{ isIntersecting }]) => {
          if (isIntersecting) {
            lazyLoaded.value = true;
            if (showSkeleton.value) {
              enableSkeletonAnimation.value = true;
            }
            stop();
          }
        }, {
          rootMargin: '100px',
        });
      unobserveFn = stop;
    }
  };

  /**
  * determine the proper fetch priority value based on provided value
  * @returns {import('vue').ComputedRef<String>} computed fetch priority value
  */
  const currentFetchPriority = computed(() => {
    // pt value take precedence over deprecated prop fetchPriority
    if (pt.value?.img?.fetchPriority) {
      return pt.value?.img?.fetchPriority;
    }
    switch (fetchPriority.value) {
    case true:
      return 'high';
    case false:
      return null;
    default:
      return fetchPriority.value;
    }
  });

  /**
  * determine the proper loading attribute to use for img
  * @returns {import('vue').ComputedRef<String>} computed loading value
  */
  const loadingAttr = computed(() => {
    // pt value take precedence over deprecated prop lazyLoad
    if (pt.value?.img?.loading) {
      return pt.value?.img?.loading;
    }
    return lazyLoad.value ? 'lazy' : null;
  });

  const containerHeight = ref(null);
  onMounted(() => {
    // pt value take precedence over deprecated prop lazyLoad
    if (pt.value?.img?.loading) {
      if (pt.value?.img?.loading === 'eager') {
        lazyLoaded.value = true;
      }
    } else if (!lazyLoad.value) {
      lazyLoaded.value = true;
    }
    useResizeObserver(pictureContainer, (entries) => {
      containerHeight.value = entries[0].contentRect.width / aspectRatio.value;
    });
    setupIntersect();
  });

  onBeforeUnmount(() => {
    if (unobserveFn) {
      unobserveFn();
    }
  });

  return {
    currentFetchPriority,
    loadingAttr,
    lazyLoaded,
    enableSkeletonAnimation,
    showSkeleton,
    pictureContainer,
    containerHeight,
  };
}
